'use client';

import React, { useState, createContext, useContext, useEffect, useCallback } from 'react';
import isEqual from 'lodash/isEqual';
import debounce from 'lodash/debounce';
import Dispatcher from '@dealroadshow/json-rpc-dispatcher';
import BondsRepository from '@/creditflowresearch/infrastructure/repository/BondsRepository';
import { getContainer } from '@framework/di/getContainer';
import { useFiltersContext } from '@/creditflowresearch/application/FiltersContext';
import { useBondsAssetClassesContext } from '@/creditflowresearch/application/Bonds/BondsAssetClassesContext';
import usePrevious from '@framework/hooks/usePrevious';
import { IInventoryItem, ISubscriptionStatus } from '@/creditflowresearch/domain/vo/Bond/IBonds';
import { IDatetime } from '@framework/date-helpers/Datetime';
import useGetBondsPayload from '@/creditflowresearch/application/Bonds/useGetBondsPayload';
import { useBondsFiltersContext } from '@/creditflowresearch/application/Bonds/BondsFiltersContext';

interface IInventories {
  collection: IInventoryItem[],
  totalCount: number,
}

export interface IInventoryData {
  inventories?: IInventories,
  lastImportAt?: IDatetime,
  subscriptionStatus?: ISubscriptionStatus,
}

export const useInventoryTable = (initial: IInventoryData, isTile?: boolean) => {
  const [inventoriesData, setInventoriesData] = useState<IInventoryData>(initial);
  const [isInventoriesFetching, setIsInventoriesFetching] = useState(false);
  const container = getContainer();
  const { filtersPayload } = useBondsFiltersContext();
  const { currentAssetClass } = useBondsAssetClassesContext();
  const { params } = useFiltersContext();
  const prevParams = usePrevious({ ...params, ...filtersPayload }) || { ...params, ...filtersPayload };

  const { getPayload } = useGetBondsPayload();

  useEffect(() => setInventoriesData(initial), [initial]);

  useEffect(() => {
    const currentParams = { ...params, ...filtersPayload };
    const shouldDenyFetch = (!currentAssetClass || !isTile || !filtersPayload)
                            && isEqual(prevParams, currentParams);
    if (shouldDenyFetch) return;
    getInventories(getPayload({}));
  }, [
    params.perPage,
    params.page,
    params.sortBy,
    params.sortOrder,
    currentAssetClass,
    filtersPayload,
  ]);

  const getInventories = async (payload) => {
    setIsInventoriesFetching(true);

    try {
      const bondsRepository = container.get(BondsRepository);
      const response = await bondsRepository.getInventoryList(payload);
      setInventoriesData(response);
    } catch (e) {
      if (Dispatcher.isAbortError(e)) {
        return;
      }
    } finally {
      setIsInventoriesFetching(false);
    }
  };

  const debouncedGetInventories = useCallback(debounce(getInventories, 500), []);

  return {
    isInventoriesFetching,
    getInventories,
    setInventoriesData,
    inventoriesData,
    debouncedGetInventories,
  };
};

type InventoryTableContextType = ReturnType<typeof useInventoryTable>;

export const InventoryTableContext = createContext<InventoryTableContextType>(null);

export const useInventoryTableContext = () => {
  const context = useContext(InventoryTableContext);
  if (!context) {
    throw new Error('useInventoryTableContext must be used within the InventoryTableContext');
  }
  return context;
};

interface IProps {
  initial: IInventoryData,
  isTile?: boolean,
  children: React.ReactNode,
}

const InventoryTableContextProvider = ({ initial, isTile, children }: IProps) => (
  <InventoryTableContext.Provider value={ useInventoryTable(initial, isTile) }>
    { children }
  </InventoryTableContext.Provider>
);

export default InventoryTableContextProvider;
